import { Timeout } from "PFTypes/timeout";
import React, { useRef } from "react";

export type Position = {
  x: number;
  y: number;
};

type UseDragAndDrop = {
  draggableElementRef: React.RefObject<HTMLDivElement | null>;
  onDragStart: (initialPosition: Position) => void;
  onDragMove: (clientPosition: Position) => void;
  onDrop: (clientPosition: Position) => void;
  calculatePosition?: (clientPosition: Position, currentPosition: Position) => Position;
  shouldMove?: (clientPosition: Position) => boolean;
  customDragOperation?: (clientPosition: Position) => void;
};

export const useDragAndDrop = ({
  draggableElementRef,
  onDragStart,
  onDragMove,
  onDrop,
  calculatePosition,
  customDragOperation,
  shouldMove = () => true
}: UseDragAndDrop) => {
  const isDragging = useRef(false);
  const timeout = useRef<Timeout>(undefined);

  const cancelMouseDown = () => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
  };

  const handleMouseDown = (event: React.MouseEvent<Element>) => {
    event.preventDefault();
    event.stopPropagation();

    const moveAt = (clientPosition: Position) => {
      if (draggableElementRef.current) {
        const currentPosition = {
          x: Number(draggableElementRef.current.style.left.replace("px", "")),
          y: Number(draggableElementRef.current.style.top.replace("px", ""))
        };

        if (shouldMove(clientPosition)) {
          if (!customDragOperation) {
            const newPosition = calculatePosition
              ? calculatePosition(clientPosition, currentPosition)
              : currentPosition;
            draggableElementRef.current.style.left = `${newPosition.x}px`;
          } else {
            customDragOperation(clientPosition);
          }

          onDragMove(clientPosition);
        }
      }
    };

    const mouseMove = (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (!isDragging.current || !draggableElementRef.current) {
        return;
      }

      const clientPosition = {
        x: event.clientX,
        y: event.clientY
      };

      moveAt(clientPosition);
    };

    const mouseUp = (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      cancelMouseDown();
      document.body.removeEventListener("mouseup", mouseUp);
      document.body.removeEventListener("mousemove", mouseMove);

      isDragging.current = false;

      const clientPosition = {
        x: event.clientX,
        y: event.clientY
      };

      onDrop(clientPosition);
    };

    document.body.addEventListener("mouseup", mouseUp);

    timeout.current = setTimeout(() => {
      if (draggableElementRef.current) {
        isDragging.current = true;

        const clientPosition = {
          x: event.clientX,
          y: event.clientY
        };

        onDragStart(clientPosition);
        document.body.addEventListener("mousemove", mouseMove);
      }
    }, 200);
  };

  return { handleMouseDown, isDragging: isDragging.current };
};
